import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useApi } from './api/api-context.js';
import { useTranslation } from './use-translation.js';

export interface SiteInfo {
  readonly tenantId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: string;
  readonly logoUrl: string;
}

interface SiteResponse {
  readonly tenantId: string;
  readonly name: string;
  readonly slug: string;
  readonly status: string;
  readonly logoUrl: string | null | undefined;
}

const SiteContext = createContext<SiteInfo | null>(null);
const SiteReadyContext = createContext<boolean>(false);

function hostnameSlug(): string {
  if (typeof window === 'undefined') return '';
  const parts = window.location.hostname.split('.');
  if (parts.length <= 1 || parts[parts.length - 1] !== 'localhost') return '';
  const first = parts[0];
  if (first === 'www') return parts[1] ?? '';
  return first ?? '';
}

function defaultSiteFromHostname(): SiteInfo {
  const slug = hostnameSlug();
  return {
    tenantId: '',
    name: slug === '' ? '' : slug.toUpperCase(),
    slug,
    status: 'ACTIVE',
    logoUrl: slug === '' ? '' : `/${slug}-logo.png`,
  };
}

function normaliseSite(response: SiteResponse): SiteInfo {
  const slug = response.slug || hostnameSlug();
  return {
    tenantId: response.tenantId,
    name: response.name || defaultSiteFromHostname().name,
    slug,
    status: response.status || 'ACTIVE',
    logoUrl: response.logoUrl ?? (slug === '' ? '' : `/${slug}-logo.png`),
  };
}

export function SiteProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const api = useApi();
  const [site, setSite] = useState<SiteInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<SiteResponse>('/v1/site')
      .then((response) => {
        if (!cancelled) setSite(normaliseSite(response));
      })
      .catch(() => {
        // Fail silently; the UI falls back to the hostname-derived default.
        if (!cancelled) setSite(defaultSiteFromHostname());
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const value = useMemo(() => site ?? defaultSiteFromHostname(), [site]);

  return (
    <SiteReadyContext.Provider value={site !== null}>
      <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
    </SiteReadyContext.Provider>
  );
}

export function useSite(): SiteInfo {
  return useContext(SiteContext) ?? defaultSiteFromHostname();
}

export function useOptionalSite(): SiteInfo | null {
  return useContext(SiteContext);
}

export function useSiteReady(): boolean {
  return useContext(SiteReadyContext);
}

const PageTitleContext = createContext<(title: string | null) => void>(() => {
  return;
});

export function PageTitleProvider({
  siteName,
  children,
}: {
  readonly siteName: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const [pageTitle, setPageTitle] = useState<string | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    const base =
      siteName === '' ? t('site.title.default') : `${siteName} ${t('site.title.suffix')}`;
    if (pageTitle === null || pageTitle === '') {
      document.title = base;
      return;
    }
    const startsWithSiteName =
      siteName !== '' && pageTitle.toLowerCase().startsWith(siteName.toLowerCase());
    document.title = startsWithSiteName ? pageTitle : `${siteName} ${pageTitle}`;
  }, [pageTitle, siteName, t]);

  return <PageTitleContext.Provider value={setPageTitle}>{children}</PageTitleContext.Provider>;
}

export function usePageTitle(title: string | null | undefined): void {
  const setPageTitle = useContext(PageTitleContext);
  useEffect(() => {
    if (title === undefined) return;
    setPageTitle(title);
    return () => {
      setPageTitle(null);
    };
  }, [title, setPageTitle]);
}
